



  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { Mixins } from 'vue-mixin-decorator';

  import dayjs from 'dayjs';

  import formatDate from '@/shared/filters/formatDate.filter.ts';
  import Input, {
    InputType,
    InputStyle,
    InputPredefinedStyle,
    InputLabelColor,
  } from '@/shared/components/input.component.vue';
  import Icon, { IconEnum } from '@/shared/components/icon.component.vue';
  import ClickOutside from '@/shared/mixins/clickOutside.mixin.ts';

  export enum DatepickerBorder {
    Grey = 'datepicker--border-grey',
    None = 'datepicker--border-none',
  }

  @Component({
    components: {
      Input,
      Icon,
    },
    filters: {
      formatDate,
    },
  })
  export default class Datepicker extends Mixins<ClickOutside> (ClickOutside) {
    @Prop({ default: '' }) private dateFrom!: string;
    @Prop({ default: '' }) private dateTo!: string;
    @Prop({ default: '' }) private dateFromPlaceholder!: string;
    @Prop({ default: '' }) private dateToPlaceholder!: string;
    @Prop({ default: DatepickerBorder.None }) private border!: DatepickerBorder;
    @Prop({ default: '' }) private limitDate!: string;
    @Prop({ default: true }) private hasRange!: boolean;

    private inputType = InputType;
    private inputStyle = InputStyle;
    private inputLabelColor = InputLabelColor;
    private inputPredefinedStyle = InputPredefinedStyle;
    private iconEnum = IconEnum;
    private shortNameDayOfWeek = [
      {
        short: 'Lun',
      },
      {
        short: 'Mar',
      },
      {
        short: 'Mié',
      },
      {
        short: 'Jue',
      },
      {
        short: 'Vie',
      },
      {
        short: 'Sáb',
      },
      {
        short: 'Dom',
      },
    ];
    private days: Day[] = [];
    private fromDaySelected: any = '';
    private toDaySelected: any = '';
    private fromDaySelectedText = '';
    private toDaySelectedText = '';
    private fromLabelColor: InputLabelColor = this.inputLabelColor.BlueDark;
    private toLabelColor: InputLabelColor = this.inputLabelColor.BlueDark;

    private currentMonth = dayjs(new Date(), {locale: 'es'});
    private isOpen: boolean = false;
    private isBlocked: boolean = false;
    private isFromSelected: boolean = true;

    private created() {
      this.constructMonth();
      this.checkClick();
      this.initilizeDatepicker();
    }

    private checkClick() {
      this.clickOutside( (e:any) => {
        this.closeDatepicker();
      }, 'datepicker');
    }

    @Watch('dateTo')
    private watchDateTo(newVal: boolean, oldVal: boolean) {
      if (newVal == null) {
        this.initilizeDatepicker()
      }
    }

    @Watch('dateFrom')
    private watchDateFrom(newVal: boolean, oldVal: boolean) {
      if (newVal == null) {
        this.initilizeDatepicker()
      }
    }

    @Watch('isOpen')
    private watchIsOpen(newVal: boolean, oldVal: boolean) {
        this.isOpen = newVal;

        this.changeInputSelected();
    }

    @Watch('fromDaySelected')
    private watchFromDaySelected(newVal: any, oldVal: any) {
      if (this.fromDaySelected) {
        this.fromDaySelectedText = this.fromDaySelected.format('DD/MM/YY');
      }
    }

    @Watch('toDaySelected')
    private watchToDaySelected(newVal: any, oldVal: any) {
      if (this.toDaySelected) {
        this.toDaySelectedText = this.toDaySelected.format('DD/MM/YY');
      }
    }


    get fromDayWhenClosing() {
      const today = dayjs();
      let date = dayjs(today, { locale: 'es'});
      if ( this.fromDaySelected ) {
        date = this.fromDaySelected;
      }
      return date;
    }

    get rightControlDisabled() {
      let disabled = false;
      if ( this.limitDate !== '' ) {
        const limitDayJs = dayjs(this.limitDate, { locale: 'es' });
        disabled = (this.currentMonth.month() === limitDayJs.month());
      }
      return disabled;
    }

    get leftLabel() {
      let label = 'DESDE';

      if ( !this.hasRange ) {
        label = 'FECHA';
      }

      return label;
    }


    private initilizeDatepicker() {
      if (this.dateFrom !== '' && this.dateFrom !== '') {
        this.fromDaySelected = dayjs(this.dateFrom, { locale: 'es' });
        this.toDaySelected = dayjs(this.dateTo, { locale: 'es' });
        this.currentMonth = this.fromDaySelected;
        this.constructMonth();
      }
    }

    private isEqualDay(currentDay: any, daySelected: any) {
      return ((currentDay.format('DD') === dayjs(daySelected).format('DD'))
          && (currentDay.format('MM') === dayjs(daySelected).format('MM'))
          && (currentDay.format('YYYY') === dayjs(daySelected).format('YYYY')));
    }

    private isDaySelected(day: any) {
      let isDayReturn = false;
      if (this.isFromSelected) {
        if (this.fromDaySelected) {
          isDayReturn = this.isEqualDay(day, this.fromDaySelected);
        }
      } else {
        if (this.toDaySelected) {
          isDayReturn = this.isEqualDay(day, this.toDaySelected);
        }
      }
      return isDayReturn;
    }

    private isDisabledDay(day: any) {
      let disabled = false;
      if (this.limitDate !== '') {
        const limitDayjs = dayjs(this.limitDate, { locale: 'es' });
        if (limitDayjs.isBefore(day)) {
          disabled = true;
        }
      }
      return disabled;
    }

    private isDayFirstSelected(day: any) {
      let isFirstDayReturn = false;
      if (this.isFromSelected) {
        if (this.toDaySelected) {
          isFirstDayReturn = this.isEqualDay(day, this.toDaySelected);
        }
      } else {
        if (this.fromDaySelected) {
          isFirstDayReturn = this.isEqualDay(day, this.fromDaySelected);
        }
      }
      return isFirstDayReturn;
    }

    private handleInput(inputContent: any) {
      if (inputContent.length >= 8) {
        if (this.isFromSelected) {
          if (this.fromDaySelected !== dayjs(inputContent)) {
            this.selectedDay(dayjs(inputContent).set('hour', 12));
          }
        } else {
          if (this.toDaySelected !== dayjs(inputContent)) {
            this.selectedDay(dayjs(inputContent).set('hour', 12));
          }
        }
      } else {
        if (this.isFromSelected) {
          this.fromDaySelected = undefined;
        } else {
          this.toDaySelected = undefined;
        }
      }
    }

    private onFocusDate(isFromSelected: boolean) {
      this.isFromSelected = isFromSelected;
      this.changeInputSelected();
    }

    private changeMonth(nextMonth: boolean) {
      if (nextMonth) {
        this.currentMonth = dayjs(this.currentMonth, {locale: 'es'}).add(1, 'month');
      } else {
        this.currentMonth = dayjs(this.currentMonth, {locale: 'es'}).subtract(1, 'month');
      }
      this.constructMonth();
    }

    private constructMonth() {
      this.days = [];
      const firstDayOfMonth = dayjs(this.currentMonth).startOf('month');
      const lastDayOfMonth = dayjs(this.currentMonth).endOf('month');
      const daysOfMonth = dayjs(this.currentMonth).daysInMonth();
      const firstDayOfWeekLastMonth = firstDayOfMonth.startOf('week').add(1, 'day');
      const dayOfWeekFirstDayOfMonth = firstDayOfMonth.day();
      const dayOfWeekLastDayOfMonth = lastDayOfMonth.day();
      const firstDayOfNextMonth = lastDayOfMonth.add(1, 'day');

      let daysForFirstWeek = dayOfWeekFirstDayOfMonth - 1;

      if (dayOfWeekFirstDayOfMonth === 0) {
        daysForFirstWeek = 6;
      }

      for (let i = 0 ; i < daysForFirstWeek ; i ++) {
        const currentDay = firstDayOfWeekLastMonth.set('hour', 12).add(i, 'day');
        this.days.push({
          day: currentDay,
          isCurrentMonth: false,
        });
      }

      for (let i = 0 ; i < daysOfMonth ; i ++) {
        const currentDay = firstDayOfMonth.set('hour', 12).add(i, 'day');
        this.days.push({
          day: currentDay,
          isCurrentMonth: true,
        });
      }

      let daysForLastWeek = 7 - dayOfWeekLastDayOfMonth;

      if (dayOfWeekLastDayOfMonth === 0) {
        daysForLastWeek = 0;
      }

      for (let i = 0 ; i < daysForLastWeek ; i ++) {
        const currentDay = firstDayOfNextMonth.set('hour', 12).add(i, 'day');
        this.days.push({
          day: currentDay,
          isCurrentMonth: false,
        });
      }
    }

    private changeInputSelected() {
      if (this.isOpen) {
          this.fromLabelColor = this.inputLabelColor.Primary;
          this.toLabelColor = this.inputLabelColor.GreyDark;

          if (!this.isFromSelected) {
            this.fromLabelColor = this.inputLabelColor.GreyDark;
            this.toLabelColor = this.inputLabelColor.Primary;
          }
        } else {
          this.fromLabelColor = this.inputLabelColor.BlueDark;
          this.toLabelColor = this.inputLabelColor.BlueDark;
        }
    }

    private mouseenter() {
      this.isOpen = true;
      this.constructMonth();
    }

    private mouseleave() {
      if (!this.isBlocked) {
        this.isOpen = false;
      }
    }

    private selectedInput(isFromSelected: boolean) {
      this.isFromSelected = isFromSelected;
      this.changeInputSelected();
      this.currentMonth = (this.isFromSelected) ? this.fromDaySelected : this.toDaySelected;
      if (!this.currentMonth) {
        this.currentMonth = dayjs(new Date(), {locale: 'es'});
      }
      this.constructMonth();
    }

    private clickDatepicker() {
      if (this.isOpen) {
        this.isBlocked = true;
      } else {
        this.isOpen = true;
      }
    }

    private clickDay(day: any) {
      this.isBlocked = true;
      this.selectedDay(day);
    }

    private selectedDay(day: any) {
      if (this.isFromSelected) {

        if (day.isSame(this.fromDaySelected)) {
          this.fromDaySelected = undefined;
          this.fromDaySelectedText = '';
        } else {
          this.fromDaySelected = day;
        }

        if ( !this.hasRange ) {
            this.closeDatepicker();
        }

        if (this.toDaySelected) {
          if (day.isBefore(this.toDaySelected)) {
            this.closeDatepicker();
          } else {
            // this.toDaySelected = undefined;
            // this.toDaySelectedText = '';
            this.isFromSelected = false;
            this.changeInputSelected();
          }
        }
      } else {

        if (day.isSame(this.toDaySelected)) {
          this.toDaySelected = undefined;
          this.toDaySelectedText = '';
        } else {
          this.toDaySelected = day;
        }

        if (this.fromDaySelected) {
          if (this.fromDaySelected.isAfter(day)) {
            this.fromDaySelected = day;
          }
          this.closeDatepicker();
          // } else {
          //   // this.fromDaySelected = undefined;
          //   // this.fromDaySelectedText = '';
          //   this.isFromSelected = true;
          //   this.changeInputSelected();
          // }
        }
      }
    }

    private closeDatepicker() {
      if (this.isOpen) {
        this.isOpen = false;
        this.isBlocked = false;
        this.isFromSelected = true;
        let from = '';
        let to = '';
        this.currentMonth = this.fromDayWhenClosing;
        if (this.fromDaySelected && this.fromDaySelected.$d) {
          from = dayjs(this.fromDaySelected).toISOString();
        }
        if (this.toDaySelected && this.toDaySelected.$d) {
          to = dayjs(this.toDaySelected).toISOString();
        }
        if ( this.hasRange ) {
          this.$emit('range-selected', from, to);
        } else {
          this.$emit('date-selected', from);
        }
      }
    }
  }

  export interface Day {
    day: any;
    isCurrentMonth: boolean;
}
